// Balance reconciliation — admin-only.
//
// GET /v1/admin/reconciliation?from=&to= returns, per rail account, the
// balance arithmetic for the window: opening → − dispatched + refunded →
// expected closing, against the actual closing reading. The residual is
// read out as credits (money that arrived) or charges (money that left
// with no payout of ours behind it), plus the list of payouts whose money
// the rail still holds.
//
// Money is bigint paise (CLAUDE.md rule 6).
import { apiClient } from "./client";
import type { DateRangeBounds } from "@/utils/dateRange";

interface RawReading {
  at: string;
  balance_minor: number | bigint;
}

interface RawHeld {
  payout_id: number;
  merchant_id: number;
  amount_minor: number | bigint;
  dispatched_at: string;
  failed_at: string;
  error_code: string;
  provider_txn_id: string;
}

interface RawReport {
  paysmart_merchant_id: string;
  merchants: number[];
  from: string;
  to: string;
  opening: RawReading | null;
  closing: RawReading | null;
  dispatched_count: number;
  dispatched_minor: number | bigint;
  refunded_count: number;
  refunded_minor: number | bigint;
  expected_closing_minor: number | bigint;
  residual_minor: number | bigint;
  credits_minor: number | bigint;
  charges_minor: number | bigint;
  charge_per_payout_minor: number | bigint;
  held: RawHeld[] | null;
  held_minor: number | bigint;
  warnings: string[] | null;
}

export interface Reading {
  at: string;
  balanceMinor: bigint;
}

export interface HeldPayout {
  payoutId: number;
  merchantId: number;
  amountMinor: bigint;
  dispatchedAt: string;
  failedAt: string;
  errorCode: string;
}

export interface ReconReport {
  /** The rail's account label, e.g. "29V2155SVGITOGND". */
  account: string;
  merchants: number[];
  from: string;
  to: string;
  opening: Reading | null;
  closing: Reading | null;
  dispatchedCount: number;
  dispatchedMinor: bigint;
  refundedCount: number;
  refundedMinor: bigint;
  expectedClosingMinor: bigint;
  residualMinor: bigint;
  creditsMinor: bigint;
  chargesMinor: bigint;
  chargePerPayoutMinor: bigint;
  held: HeldPayout[];
  heldMinor: bigint;
  warnings: string[];
}

export interface ReconOverview {
  from: string;
  to: string;
  reports: ReconReport[];
  maxChargePerPayoutMinor: bigint;
}

function reading(r: RawReading | null): Reading | null {
  return r ? { at: r.at, balanceMinor: BigInt(r.balance_minor) } : null;
}

export async function getReconciliation(
  range: DateRangeBounds = {},
): Promise<ReconOverview> {
  const resp = await apiClient.get<{
    from: string;
    to: string;
    reports: RawReport[] | null;
    max_charge_per_payout_minor: number | bigint;
  }>("/v1/admin/reconciliation", {
    params: { from: range.from, to: range.to },
  });
  const d = resp.data;
  return {
    from: d.from,
    to: d.to,
    maxChargePerPayoutMinor: BigInt(d.max_charge_per_payout_minor),
    reports: (d.reports ?? []).map((r) => ({
      account: r.paysmart_merchant_id,
      merchants: r.merchants ?? [],
      from: r.from,
      to: r.to,
      opening: reading(r.opening),
      closing: reading(r.closing),
      dispatchedCount: r.dispatched_count,
      dispatchedMinor: BigInt(r.dispatched_minor),
      refundedCount: r.refunded_count,
      refundedMinor: BigInt(r.refunded_minor),
      expectedClosingMinor: BigInt(r.expected_closing_minor),
      residualMinor: BigInt(r.residual_minor),
      creditsMinor: BigInt(r.credits_minor),
      chargesMinor: BigInt(r.charges_minor),
      chargePerPayoutMinor: BigInt(r.charge_per_payout_minor),
      held: (r.held ?? []).map((h) => ({
        payoutId: h.payout_id,
        merchantId: h.merchant_id,
        amountMinor: BigInt(h.amount_minor),
        dispatchedAt: h.dispatched_at,
        failedAt: h.failed_at,
        errorCode: h.error_code,
      })),
      heldMinor: BigInt(r.held_minor),
      warnings: r.warnings ?? [],
    })),
  };
}

export async function runDailyReconciliation(day: string): Promise<void> {
  await apiClient.post("/v1/admin/reconciliation/run", null, {
    params: { day },
  });
}

export async function readBalanceNow(): Promise<void> {
  await apiClient.post("/v1/admin/reconciliation/read-balance");
}
